import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, Gift } from "lucide-react";
import { useCart } from "../context/CartContext";

const fmt = (n) => `$${n.toFixed(2)} AUD`;

const CartDrawer = () => {
    const { open, setOpen, items, remove, setQty, totals, giftBox, setGiftBox } = useCart();
    const navigate = useNavigate();

    useEffect(() => {
        if (open) document.body.classList.add("no-scroll");
        else document.body.classList.remove("no-scroll");
        return () => document.body.classList.remove("no-scroll");
    }, [open]);

    return (
        <>
            {/* Overlay */}
            <div
                onClick={() => setOpen(false)}
                className={`fixed inset-0 z-50 bg-black/30 transition-opacity duration-300 ${
                    open ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                aria-hidden="true"
            />
            <aside
                data-testid="cart-drawer"
                className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[440px] bg-[#FAF9F6] shadow-xl transform transition-transform duration-400 ease-out ${
                    open ? "translate-x-0" : "translate-x-full"
                }`}
                aria-label="Shopping cart"
            >
                <div className="h-full flex flex-col">
                    <div className="flex items-center justify-between px-6 h-20 border-b border-[#E3DED5]">
                        <h2 className="font-serif text-2xl">Your Cart</h2>
                        <button
                            onClick={() => setOpen(false)}
                            data-testid="close-cart-button"
                            aria-label="Close cart"
                        >
                            <X size={20} strokeWidth={1.4} />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
                        {items.length === 0 ? (
                            <div className="text-center py-20" data-testid="empty-cart">
                                <p className="font-serif text-2xl text-[#1C1C1A]">
                                    Your cart is quiet.
                                </p>
                                <p className="mt-3 text-[#4A4A46]">
                                    Beautiful bars are waiting.
                                </p>
                                <Link
                                    to="/shop"
                                    onClick={() => setOpen(false)}
                                    className="btn-primary mt-8 inline-flex"
                                    data-testid="empty-cart-shop-link"
                                >
                                    Shop Soap
                                </Link>
                            </div>
                        ) : (
                            items.map((item) => (
                                <div
                                    key={item.slug}
                                    className="flex gap-4"
                                    data-testid={`cart-item-${item.slug}`}
                                >
                                    <div className="w-20 h-24 product-image-wrap shrink-0">
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="flex-1 flex flex-col">
                                        <div className="flex justify-between gap-2">
                                            <p className="font-serif text-lg leading-tight">
                                                {item.name}
                                            </p>
                                            <button
                                                onClick={() => remove(item.slug)}
                                                className="text-[#737562] hover:text-[#1C1C1A] text-xs uppercase tracking-[0.18em]"
                                                data-testid={`remove-item-${item.slug}`}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                        <p className="text-sm text-[#4A4A46] mt-1">
                                            {fmt(item.price)}
                                        </p>
                                        <div className="mt-auto flex items-center justify-between pt-3">
                                            <div className="inline-flex items-center border border-[#C7C0B3]">
                                                <button
                                                    onClick={() => setQty(item.slug, item.quantity - 1)}
                                                    className="w-8 h-8 flex items-center justify-center text-[#4A4A46] hover:text-[#1C1C1A]"
                                                    data-testid={`qty-decrease-${item.slug}`}
                                                    aria-label="Decrease quantity"
                                                >
                                                    <Minus size={14} />
                                                </button>
                                                <span
                                                    className="w-8 text-center text-sm"
                                                    data-testid={`qty-${item.slug}`}
                                                >
                                                    {item.quantity}
                                                </span>
                                                <button
                                                    onClick={() => setQty(item.slug, item.quantity + 1)}
                                                    className="w-8 h-8 flex items-center justify-center text-[#4A4A46] hover:text-[#1C1C1A]"
                                                    data-testid={`qty-increase-${item.slug}`}
                                                    aria-label="Increase quantity"
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                            <p className="text-sm font-medium">
                                                {fmt(item.price * item.quantity)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {items.length > 0 && (
                        <div className="border-t border-[#E3DED5] px-6 py-6 space-y-4">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={giftBox}
                                    onChange={(e) => setGiftBox(e.target.checked)}
                                    data-testid="gift-box-toggle"
                                    className="accent-[#1C1C1A]"
                                />
                                <Gift size={16} strokeWidth={1.4} className="text-[#6A7059]" />
                                <span className="text-sm">
                                    Add gift box wrapping
                                    <span className="text-[#737562]"> &nbsp;+$8.00</span>
                                </span>
                            </label>

                            <div className="text-sm space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-[#4A4A46]">Subtotal</span>
                                    <span data-testid="cart-subtotal">{fmt(totals.subtotal)}</span>
                                </div>
                                {totals.bundleDiscount > 0 && (
                                    <div className="flex justify-between text-[#6A7059]">
                                        <span>
                                            Bundle &middot; {totals.itemCount >= 5 ? "5 bars, save 20%" : "3 bars, save 15%"}
                                        </span>
                                        <span data-testid="cart-bundle-discount">
                                            -{fmt(totals.bundleDiscount)}
                                        </span>
                                    </div>
                                )}
                                {giftBox && (
                                    <div className="flex justify-between">
                                        <span className="text-[#4A4A46]">Gift box</span>
                                        <span>{fmt(totals.giftBoxFee)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span className="text-[#4A4A46]">Shipping</span>
                                    <span>
                                        {totals.shipping === 0 ? "Free" : fmt(totals.shipping)}
                                    </span>
                                </div>
                                <div className="border-t border-[#E3DED5] pt-3 mt-2 flex justify-between text-base font-medium">
                                    <span>Total</span>
                                    <span data-testid="cart-total">{fmt(totals.total)}</span>
                                </div>
                            </div>

                            <p
                                className="text-[11px] leading-relaxed text-[#4A4A46] mt-1"
                                data-testid="cart-checkout-note"
                            >
                                Have a personal price code? You can enter it on the checkout page. Darwin locals can also select Free Local Pickup at checkout.
                            </p>

                            <button
                                data-testid="checkout-button"
                                onClick={() => {
                                    setOpen(false);
                                    navigate("/checkout");
                                }}
                                className="btn-primary w-full"
                            >
                                Checkout
                            </button>

                            <div className="flex gap-2 pt-1">
                                <div className="flex-1 h-9 border border-[#C7C0B3] flex items-center justify-center text-[10px] uppercase tracking-[0.22em] text-[#4A4A46]">
                                    Apple Pay
                                </div>
                                <div className="flex-1 h-9 border border-[#C7C0B3] flex items-center justify-center text-[10px] uppercase tracking-[0.22em] text-[#4A4A46]">
                                    Google Pay
                                </div>
                                <div className="flex-1 h-9 border border-[#C7C0B3] flex items-center justify-center text-[10px] uppercase tracking-[0.22em] text-[#4A4A46]">
                                    Shop Pay
                                </div>
                            </div>
                            <p className="text-[11px] text-[#737562] text-center mt-1">
                                Free shipping over $80 within Australia.
                            </p>
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
};

export default CartDrawer;
